import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { pontoService, colaboradorService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthColaborador } from '../../contexts/AuthColaboradorContext';
import LocationModal from '../../components/LocationModal';

const PontoHistorico = () => {
  const { user } = useAuth();
  const { colaborador } = useAuthColaborador();

  const currentUser = user || colaborador;
  const [selectedPonto, setSelectedPonto] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    tipo: '',
    colaborador: ''
  });

  const veTodos = ['administrador', 'supervisor', 'coordenador', 'financeiro', 'administrativo'].includes(currentUser?.perfil);

  const { data: colaboradoresData } = useQuery({
    queryKey: ['colaboradores-lista'],
    queryFn: async () => {
      const response = await colaboradorService.listar({ limit: 500 });
      return response.data;
    },
    enabled: veTodos,
  });

  const colaboradores = colaboradoresData?.colaboradores || colaboradoresData?.data || [];

  const { data: pontosData, isLoading, error, refetch } = useQuery({
    queryKey: ['meus-pontos', currentUser?.id, filtros.colaborador],
    queryFn: async () => {
      if (!currentUser?.id) return { pontos: [] };
      try {
        const params = {};
        if (filtros.colaborador) params.colaborador = filtros.colaborador;
        const response = await pontoService.meusPontos(params);
        return response.data || { pontos: [], total: 0 };
      } catch (err) {
        console.error('Erro ao buscar pontos:', err);
        return { pontos: [], total: 0 };
      }
    },
    enabled: !!currentUser?.id,
    refetchInterval: 30000,
    retry: 1,
  });

  const pontos = pontosData?.pontos || [];

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getTipoPonto = (tipo) => {
    const tipos = {
      'batida': 'Batida de Ponto',
      'viagem-inicio': 'Início de Viagem',
      'viagem-fim': 'Fim de Viagem'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoColor = (tipo) => {
    const cores = {
      'batida': 'bg-indigo-50 text-indigo-700 border-indigo-100',
      'viagem-inicio': 'bg-green-50 text-green-700 border-green-100',
      'viagem-fim': 'bg-red-50 text-red-700 border-red-100'
    };
    return cores[tipo] || 'bg-gray-50 text-gray-700 border-gray-100';
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'batida': '⏰',
      'viagem-inicio': '🚀',
      'viagem-fim': '🏁'
    };
    return icons[tipo] || '📌';
  };

  const handleVerLocalizacao = (ponto) => {
    setSelectedPonto(ponto);
    setIsLocationModalOpen(true);
  };

  const pontosFiltrados = pontos.filter(ponto => {
    if (filtros.dataInicio && new Date(ponto.createdAt) < new Date(filtros.dataInicio)) return false;
    if (filtros.dataFim && new Date(ponto.createdAt) > new Date(filtros.dataFim)) return false;
    if (filtros.tipo && ponto.tipo !== filtros.tipo) return false;
    return true;
  });

  const handleExportar = () => {
    if (pontosFiltrados.length === 0) return;
    const dados = pontosFiltrados.map(ponto => ({
      Colaborador: ponto.colaborador?.nome || '',
      Data: formatDate(ponto.createdAt),
      Horário: formatTime(ponto.createdAt),
      Tipo: getTipoPonto(ponto.tipo),
      Latitude: ponto.localizacao.latitude,
      Longitude: ponto.localizacao.longitude,
      Observações: ponto.observacoes || ''
    }));
    const csv = [
      Object.keys(dados[0]).join(','),
      ...dados.map(row => Object.values(row).join(','))
    ].join('\\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico-pontos-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Dados exportados com sucesso!');
  };

  if (error) {
    console.error('Erro ao carregar histórico:', error);
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <p className="text-red-800 font-semibold">Erro ao carregar histórico de pontos</p>
          <p className="text-red-600 text-sm mt-1">
            {error?.response?.data?.message || error?.message || 'Ocorreu um erro ao buscar os pontos'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:opacity-90 transition"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 rounded-2xl p-6 md:p-8 text-white shadow-xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-indigo-300 text-sm font-medium uppercase tracking-widest mb-1">Registros</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Histórico de Pontos</h1>
            <p className="text-indigo-200 mt-1 text-sm">Todas as batidas registradas no sistema</p>
          </div>
          <button
            onClick={handleExportar}
            disabled={pontosFiltrados.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 rounded-xl text-white font-medium disabled:opacity-40 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="h-5 w-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
          </svg>
          Filtros
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Data Início</label>
            <input type="date" value={filtros.dataInicio}
              onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Data Fim</label>
            <input type="date" value={filtros.dataFim}
              onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tipo</label>
            <select value={filtros.tipo}
              onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500">
              <option value="">Todos os tipos</option>
              <option value="batida">Batida de Ponto</option>
              <option value="viagem-inicio">Início de Viagem</option>
              <option value="viagem-fim">Fim de Viagem</option>
            </select>
          </div>
          {veTodos && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Colaborador</label>
              <select value={filtros.colaborador}
                onChange={(e) => setFiltros({ ...filtros, colaborador: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="">Todos</option>
                {colaboradores.map((col) => (
                  <option key={col._id} value={col._id}>{col.nome}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="mt-4">
          <button
            onClick={() => setFiltros({ dataInicio: '', dataFim: '', tipo: '', colaborador: '' })}
            className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Lista de Pontos */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Registros</h3>
          <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold">
            {pontosFiltrados.length} de {pontos.length}
          </span>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent mx-auto"></div>
            <p className="text-gray-500 mt-3">Carregando histórico...</p>
          </div>
        ) : pontosFiltrados.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-gray-500">
              {pontos.length === 0 ? 'Nenhum registro encontrado' : 'Nenhum registro corresponde aos filtros'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pontosFiltrados.map((ponto) => (
              <div key={ponto._id} className="p-5 hover:bg-indigo-50/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xl shrink-0">
                      {getTipoIcon(ponto.tipo)}
                    </div>
                    <div>
                      {ponto.colaborador?.nome && (
                        <p className="font-semibold text-indigo-700 text-sm">{ponto.colaborador.nome}</p>
                      )}
                      <p className="font-bold text-gray-900">{formatDate(ponto.createdAt)}</p>
                      <p className="text-sm text-gray-500 tabular-nums">{formatTime(ponto.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${getTipoColor(ponto.tipo)}`}>
                      {getTipoPonto(ponto.tipo)}
                    </span>
                    <button
                      onClick={() => handleVerLocalizacao(ponto)}
                      className="flex items-center gap-2 px-3 py-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Localização
                    </button>
                  </div>
                </div>
                {ponto.observacoes && (
                  <div className="mt-3 pl-15 md:pl-[72px]">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Observações:</span> {ponto.observacoes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        ponto={selectedPonto}
      />
    </div>
  );
};

export default PontoHistorico;
